// zeitskala.ts — einstellbare Zeitskala des Kalender-Rasters (`/termine`, T-31).
//
// Die Nutzerin wählt über das Zahnrad „Einstellungen für den Kalender" 15, 30
// oder 60 Minuten. Die Skala steuert Darstellung UND Einrasten: `step` ist die
// Slot-Länge (Drag-to-Create, Verschieben, Resize rasten darauf ein),
// `timeslots` die Anzahl Slots je Stundengruppe, `stundePx` die Höhe einer
// Stunde (CSS-Variable `--cal-stunde-px` in calendar.css) und
// `zeitzeileAbMinuten` die Termin-Dauer, ab der der Block die zweite Zeile
// (Zeit/Ort) bekommt — die muss zur Stundenhöhe passen (≥ ~40px Blockhöhe).
//
// 15 Minuten ist das heutige Verhalten (SP2/A4, 88px/Stunde) und bleibt Default.

export type Zeitskala = 15 | 30 | 60;

export const ZEITSKALEN: readonly Zeitskala[] = [15, 30, 60];

export const DEFAULT_ZEITSKALA: Zeitskala = 15;

export interface KalenderRaster {
    step: number;
    timeslots: number;
    stundePx: number;
    zeitzeileAbMinuten: number;
}

const RASTER: Record<Zeitskala, KalenderRaster> = {
    // Heutiges Verhalten — Regressionsanker (Legacy-Mounts nutzen dieselben Werte).
    15: { step: 15, timeslots: 4, stundePx: 88, zeitzeileAbMinuten: 30 },
    // 30-min-Block = 28px (einzeilig), ab 45 min (42px) mit Zeitzeile.
    30: { step: 30, timeslots: 2, stundePx: 56, zeitzeileAbMinuten: 45 },
    // 40px/Stunde: ein Arbeitstag 07–19 Uhr passt ohne Scrollen auf den Schirm.
    60: { step: 60, timeslots: 1, stundePx: 40, zeitzeileAbMinuten: 60 },
};

/** Gespeicherten Wert lesen — Whitelist, alles Unbekannte wird zum Default. */
export function parseZeitskala(raw: string | null | undefined): Zeitskala {
    const n = Number(raw);
    return (ZEITSKALEN as readonly number[]).includes(n) ? (n as Zeitskala) : DEFAULT_ZEITSKALA;
}

/** Raster-Werte für OutlookKalender zur gewählten Skala. */
export function rasterFuerSkala(skala: Zeitskala): KalenderRaster {
    return RASTER[skala] ?? RASTER[DEFAULT_ZEITSKALA];
}

export const ZEITSKALA_KEY = "calendar.zeitskala";

/** Skala aus dem Storage lesen (Storage injizierbar, damit testbar ohne jsdom). */
export function readStoredZeitskala(
    storage: Pick<Storage, "getItem"> | undefined = typeof localStorage !== "undefined"
        ? localStorage
        : undefined,
): Zeitskala {
    try {
        return parseZeitskala(storage?.getItem(ZEITSKALA_KEY));
    } catch {
        return DEFAULT_ZEITSKALA;
    }
}
